import { useMemo, useState } from 'react';
import { Buildings, ChartPieSlice, FilePdf, ListBullets, Receipt, Scales } from '@phosphor-icons/react';
import { Chip, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, ToastPill, useToastPill, type Ton } from '../../../../../components/immobilien/ui';
import { exportPdfReport } from '../../../../../lib/export/exportPdf';
import { generateFileName, type ExportColumn, type ExportConfig } from '../../../../../lib/export/types';
import { buildMietaufstellungConfig, buildMietuebersichtConfig, computeMietKennzahlen } from '../../../../../lib/export/mietberichte';
import { berichtZeilen, type BerichtZeile } from '../../../../../lib/immobilien/bericht';
import { eur } from '../../../../../lib/immobilien/format';
import { BUCHHALTUNG_FOLGT } from '../../../../../lib/immobilien/hinweise';
import { useObjektSeite } from '../ObjektSeitenKontext';

type BerichtKey = 'objektuebersicht' | 'mietuebersicht' | 'mietaufstellung' | 'anlagev' | 'euer';

const BERICHTE: readonly { key: BerichtKey; titel: string; text: string; icon: typeof Buildings; verfuegbar: boolean }[] = [
  { key: 'objektuebersicht', titel: 'Objektübersicht', icon: Buildings, verfuegbar: true,
    text: 'Stammdaten des Objekts und eine Übersicht über die Einheiten und Mietverhältnisse.' },
  { key: 'mietuebersicht', titel: 'Mietübersicht', icon: ChartPieSlice, verfuegbar: true,
    text: 'Stammdaten und eine Übersicht über alle Mieteinnahmen (Soll).' },
  { key: 'mietaufstellung', titel: 'Mietaufstellung (einfach)', icon: ListBullets, verfuegbar: true,
    text: 'Mietaufstellung aller aktuellen Mieter. Häufige Nutzung: monatlicher Report an die Bank.' },
  { key: 'anlagev', titel: 'Anlage V', icon: Receipt, verfuegbar: false,
    text: 'Vorausgefüllte Anlage V mit Übersicht der zugehörigen Umsätze.' },
  { key: 'euer', titel: 'EÜR', icon: Scales, verfuegbar: false,
    text: 'Einnahmen-Überschuss-Rechnung mit Übersicht der zugehörigen Umsätze.' },
];

const SPALTEN: ExportColumn[] = [
  { key: 'bezeichnung', label: 'Bezeichnung' },
  { key: 'einheit_typ', label: 'Typ', format: (_v, r: BerichtZeile) => r.einheit?.typ || '' },
  { key: 'einheit_wohnflaeche', label: 'Wohnfläche', format: (_v, r: BerichtZeile) => (r.einheit?.wohnflaeche != null ? `${r.einheit.wohnflaeche.toLocaleString('de-DE')} m²` : '') },
  { key: 'einheit_etage', label: 'Etage', format: (_v, r: BerichtZeile) => r.einheit?.etage || '' },
  { key: 'einheit', label: 'Status', format: (_v, r: BerichtZeile) => r.einheit?.status || '' },
  { key: 'mieter', label: 'Mieter', format: (_v, r: BerichtZeile) => (r.mieter ? ((r.mieter.firma_name ?? '').trim() || `${r.mieter.vorname ?? ''} ${r.mieter.nachname ?? ''}`.trim()) : '') },
];

/**
 * Berichtscenter (Panel „bericht“): PDF-Berichte wie bisher (Objektübersicht, Mietübersicht,
 * Mietaufstellung). Anlage V und EÜR kommen mit der Buchhaltung. Gegenüber früher ohne die
 * wirkungslose Jahreszahl und ohne den immer leeren Saldo; gekündigte, noch wohnende Mieter zählen mit.
 */
export function BerichtPanel() {
  const { daten, name, adresse } = useObjektSeite();
  const [gewaehlt, setGewaehlt] = useState<BerichtKey | null>(null);
  const [toast, zeige] = useToastPill();
  const zeilen = useMemo(() => berichtZeilen(daten.einheiten, daten.mieter), [daten.einheiten, daten.mieter]);
  const k = useMemo(() => computeMietKennzahlen(zeilen), [zeilen]);
  const o = daten.objekt;

  const erstelle = () => {
    if (!gewaehlt) return;
    const info = { name, objektnummer: o.objektnummer, adresse: adresse || 'Keine Adresse' };
    try {
      if (gewaehlt === 'objektuebersicht') {
        const config: ExportConfig = {
          title: name,
          columns: SPALTEN,
          data: zeilen,
          fileName: generateFileName(`${name.replace(/\s+/g, '_')}_Bericht`),
          stats: { items: [
            { label: 'Objektnummer', value: o.objektnummer || '—' },
            { label: 'Adresse', value: info.adresse },
            { label: 'Typ', value: o.objekttyp || '—' },
            { label: 'Einheiten', value: zeilen.length },
            { label: 'Vermietet', value: zeilen.filter(z => z.mieter).length },
          ] },
        };
        exportPdfReport(config);
      } else if (gewaehlt === 'mietuebersicht') {
        exportPdfReport(buildMietuebersichtConfig(info, zeilen));
      } else if (gewaehlt === 'mietaufstellung') {
        exportPdfReport(buildMietaufstellungConfig(info, zeilen));
      } else {
        return;
      }
      zeige(`${BERICHTE.find(b => b.key === gewaehlt)?.titel ?? 'Bericht'} erstellt`);
    } catch (e) {
      zeige(`Bericht konnte nicht erstellt werden: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    }
  };

  const ton = (an: boolean): Ton => (an ? 'acc' : 'neutral');

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile label="Einheiten" wert={k.einheiten} sub={`${k.vermietet} vermietet · ${k.leer} frei`} icon={Buildings} ton="acc" />
        <KpiTile label="Kaltmiete / Monat" wert={eur(k.summeKalt)} sub={`warm ${eur(k.summeWarm)}`} />
        <KpiTile label="Jahres-Soll" wert={eur(k.jahresSoll, { nachkomma: 0 })} sub="Warmmiete × 12" />
      </KpiReihe>
      <InnenKarte>
        <KartenTitel icon={FilePdf}>Bericht auswählen</KartenTitel>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(220px,100%),1fr))', gap: 10 }}>
          {BERICHTE.map(b => {
            const an = gewaehlt === b.key;
            const IconKomp = b.icon;
            return (
              <button key={b.key} type="button" role="radio" aria-checked={an} disabled={!b.verfuegbar} onClick={() => setGewaehlt(b.key)}
                className={`flex flex-col text-left ${b.verfuegbar ? 'cursor-pointer immo-hover-flaeche' : 'cursor-default'}`}
                style={{ gap: 8, padding: 14, borderRadius: 18, border: 'none', color: 'var(--ink)', opacity: b.verfuegbar ? 1 : 0.6,
                  background: an ? 'var(--bg)' : 'var(--fill)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
                <span className="flex items-center justify-between" style={{ gap: 8 }}>
                  <span className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 12, background: an ? 'var(--acc-soft)' : 'var(--fill2)' }}>
                    <IconKomp weight="fill" size={18} color={an ? 'var(--acc)' : 'var(--ink3)'} />
                  </span>
                  {!b.verfuegbar && <Chip groesse="xs" ton="neutral">{BUCHHALTUNG_FOLGT}</Chip>}
                  {b.verfuegbar && an && <Chip groesse="xs" ton={ton(an)}>Ausgewählt</Chip>}
                </span>
                <span style={{ fontSize: 15, fontWeight: 800 }}>{b.titel}</span>
                <span style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.45 }}>{b.text}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Der Bericht wird als PDF heruntergeladen.</span>
          <ImmoButton art="akzent" icon={FilePdf} disabled={!gewaehlt} onClick={erstelle}>Bericht erstellen</ImmoButton>
        </div>
      </InnenKarte>
    </>
  );
}
